import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Energy } from '@sorare/core/src/atoms/icons/Energy';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { useEnergyBalance } from '@sorare/core/src/hooks/useEnergyBalance';
import { glossary } from '@sorare/core/src/lib/glossary';

import type {
  AsideEnergyBalance_So5Leaderboard,
  AsideEnergyBalance_So5LineupPreview,
} from './__generated__/index.graphql';

const ErrorWrapper = styled(Vertical)`
  align-items: end;
`;

type Props = {
  so5LineupPreview?: AsideEnergyBalance_So5LineupPreview;
  so5Leaderboard: AsideEnergyBalance_So5Leaderboard;
};

export const AsideEnergyBalance = ({
  so5LineupPreview,
  so5Leaderboard,
}: Props) => {
  const { mainRarityType } = so5Leaderboard || {};
  const { energyBalance } = useEnergyBalance();

  if (
    !mainRarityType ||
    mainRarityType === Rarity.common ||
    mainRarityType === Rarity.custom_series
  ) {
    return null;
  }

  const scarcities = {
    [Rarity.limited]: InGameCurrency.LIMITED_ENERGY,
    [Rarity.rare]: InGameCurrency.RARE_ENERGY,
    [Rarity.super_rare]: InGameCurrency.SUPER_RARE_ENERGY,
    [Rarity.unique]: InGameCurrency.UNIQUE_ENERGY,
  } as Record<Rarity, InGameCurrency>;

  const allowedRarities = so5Leaderboard?.rules?.rarities;

  return (
    <SBHorizontal>
      <LabelL bold>
        <FormattedMessage {...glossary.energy} />
      </LabelL>
      <Horizontal>
        {allowedRarities?.map(rarity => {
          if (
            !rarity ||
            rarity === Rarity.common ||
            rarity === Rarity.custom_series
          ) {
            return null;
          }
          const rarityTyped = rarity as Rarity;

          const balance = energyBalance.find(
            eb => eb.currency === scarcities[rarityTyped]
          )?.amount;

          const consumed = so5LineupPreview?.consumedEnergies?.find(
            c => c.currency === scarcities[rarityTyped]
          )?.amount;

          return (
            <ErrorWrapper key={rarity} gap={0.5}>
              {(consumed || 0) > (balance || 0) && (
                <LabelL color="var(--c-red-400)">
                  <FormattedMessage
                    id="AsideEnergyBalance.notEnoughEnergy"
                    defaultMessage="Not enough energy"
                  />
                </LabelL>
              )}
              <Horizontal gap={0.5}>
                <Energy size={2} variant={rarityTyped} />
                <LabelL bold>{balance}</LabelL>
                {!!consumed && (
                  <LabelL
                    color={
                      consumed < 0
                        ? 'var(--c-red-400)'
                        : 'var(--c-onBase-success)'
                    }
                  >
                    ({consumed < 0 ? '' : '+'}
                    {consumed})
                  </LabelL>
                )}
              </Horizontal>
            </ErrorWrapper>
          );
        })}
      </Horizontal>
    </SBHorizontal>
  );
};

AsideEnergyBalance.fragments = {
  so5LineupPreview: gql`
    fragment AsideEnergyBalance_So5LineupPreview on So5LineupPreview {
      consumedEnergies {
        currency
        amount
      }
    }
  ` as TypedDocumentNode<AsideEnergyBalance_So5LineupPreview>,
  so5Leaderboard: gql`
    fragment AsideEnergyBalance_So5Leaderboard on So5Leaderboard {
      slug
      mainRarityType
      rules {
        id
        rarities
      }
    }
  ` as TypedDocumentNode<AsideEnergyBalance_So5Leaderboard>,
};
